import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Tag, DollarSign, Receipt } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import apiClient from '@/services/api';
import type { DiscountReport as DiscountReportData } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

/**
 * Discount: per-ticket discount detail plus summary totals (grand total and a
 * by-discount-name breakdown), mirroring the POS Discount Report. Replaces the
 * old discount-by-order-type view.
 */
export default function DiscountReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<DiscountReportData>({
    queryKey: ['discount-report', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getDiscountReport(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const totalDiscount = data?.totalDiscount ?? 0;
  const grossSales = data?.grossSales ?? 0;
  const discountPct = grossSales > 0 ? (totalDiscount / grossSales) * 100 : 0;
  const discountedTickets = data?.discountedTicketCount ?? 0;
  const perTicket = data?.perTicket ?? [];
  const byName = data?.byDiscountName ?? [];
  const days = (data?.byDay ?? []).map((d) => ({ date: format(parseISO(d.date), 'MMM dd'), discount: Number(d.amount) }));
  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });

  const perTicketHeaders = ['Ticket', 'Date', 'Order Type', 'Discount', 'Amount'];
  const perTicketRows = perTicket.map((r) => [
    r.ticketNumber ?? String(r.posTicketId),
    r.date ? format(parseISO(r.date), 'yyyy-MM-dd') : '',
    r.orderType ?? '',
    r.discountName || '—',
    formatCurrency(r.discountAmount),
  ]);
  const summaryHeaders = ['Discount', 'Count', 'Amount'];
  const summaryRows = byName.map((r) => [r.name, r.count == null ? '' : String(r.count), formatCurrency(r.amount)]);
  const summaryFooter = ['TOTAL', String(discountedTickets), formatCurrency(totalDiscount)];

  const sections = (): ReportSection[] => [
    { heading: 'Discount by Name', headers: summaryHeaders, rows: [...summaryRows, summaryFooter] },
    { heading: 'Discounts by Ticket', headers: perTicketHeaders, rows: perTicketRows },
  ];
  const kpis = (): [string, string][] => [
    ['Total Discount', formatCurrency(totalDiscount)],
    ['Discount %', `${discountPct.toFixed(1)}%`],
    ['Discounted Tickets', String(discountedTickets)],
  ];

  return (
    <ReportFrame
      title="Discount"
      description="Discounts per ticket plus summary totals by discount name, matching the POS Discount Report."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={discountedTickets === 0}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`discount-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('Discount', meta(), kpis(), sections()))}
      onExportPdf={() => data && printGenericReport('Discount Report', meta(), kpis(), sections())}
    >
      <div className="grid grid-cols-3 gap-4">
        <KPICard label="Total Discount" value={formatCurrency(totalDiscount)} icon={Tag} palette="purple" />
        <KPICard label="Discount %" value={`${discountPct.toFixed(1)}%`} icon={DollarSign} palette="blue" />
        <KPICard label="Discounted Tickets" value={String(discountedTickets)} icon={Receipt} palette="green" />
      </div>

      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">Discount by Day</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={days}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="discount" name="Discount" fill="#8884D8" />
          </BarChart>
        </ResponsiveContainer>
      </div></div>

      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">Discount by Name</h3>
        <SimpleTable headers={summaryHeaders} rows={summaryRows} footer={summaryFooter} />
      </div></div>

      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">Discounts by Ticket</h3>
        <SimpleTable headers={perTicketHeaders} rows={perTicketRows} />
      </div></div>
    </ReportFrame>
  );
}
